// ORCA360 - CVR-Style Audio Record Button Component
// Single unified recorder with integrated phase selector
// Features: phase selector pills, idle ring, CVR spinning ring, audio level reactivity,
// always-visible timer, wake lock + haptic (via hook), Record More / Done flow

import { useState, useCallback, useRef } from 'react';
import { useAudioRecorder, type RecorderError } from '../hooks/useAudioRecorder';
import {
  uploadAudioChunk,
  uploadCompleteRecording,
  registerRecording,
  triggerTranscription,
  concatenateRecordings,
  type RecordingPhase,
} from '../lib/audioUpload';

interface RecordButtonProps {
  sessionId: string;
  /** Pre-select a phase and hide the phase selector (used by wizard steps) */
  defaultPhase?: RecordingPhase;
  onRecordingComplete?: (recordingId: string, phase: RecordingPhase) => void;
  onRecordingStart?: () => void;
  onError?: (error: string) => void;
}

const PHASES: { key: RecordingPhase; label: string; icon: string }[] = [
  { key: 'briefing', label: 'Briefing', icon: '\ud83d\udccb' },
  { key: 'simulator', label: 'Simulator', icon: '\u2708\ufe0f' },
  { key: 'debriefing', label: 'Debriefing', icon: '\ud83d\udcdd' },
];

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// Inject CVR keyframe animations once into the document
const CVR_STYLES_ID = 'cvr-record-button-styles';
function ensureCVRStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(CVR_STYLES_ID)) return;

  const style = document.createElement('style');
  style.id = CVR_STYLES_ID;
  style.textContent = `
    @keyframes cvr-ring-spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes cvr-glow {
      0%, 100% { box-shadow: 0 0 20px rgba(239, 68, 68, 0.3); }
      50% { box-shadow: 0 0 40px rgba(239, 68, 68, 0.6); }
    }
    @keyframes cvr-rec-blink {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.3; }
    }
  `;
  document.head.appendChild(style);
}

export default function RecordButton({
  sessionId,
  defaultPhase,
  onRecordingComplete,
  onRecordingStart,
  onError,
}: RecordButtonProps) {
  ensureCVRStyles();

  const initialPhase = defaultPhase || 'simulator';
  const [selectedPhase, setSelectedPhase] = useState<RecordingPhase>(initialPhase);
  const activePhaseRef = useRef<RecordingPhase>(initialPhase);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [chunksUploaded, setChunksUploaded] = useState(0);
  const chunksUploadedRef = useRef(0);

  // Multi-segment "Record More" state
  const chunkOffsetRef = useRef(0);
  const [totalDurationSeconds, setTotalDurationSeconds] = useState(0);
  const [segmentCount, setSegmentCount] = useState(0);
  const [finalized, setFinalized] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  // Track whether we have at least one recorded segment (locks phase selector)
  const hasSegmentRef = useRef(false);
  // Store the last complete blob + duration for "Done" upload
  const lastBlobRef = useRef<Blob | null>(null);
  const lastSegmentDurationRef = useRef(0);

  const handleChunkAvailable = useCallback(
    async (chunk: Blob, index: number) => {
      try {
        const offsetIndex = index + chunkOffsetRef.current;
        await uploadAudioChunk({ chunk, index: offsetIndex, sessionId, phase: activePhaseRef.current });
        chunksUploadedRef.current += 1;
        setChunksUploaded(chunksUploadedRef.current);
      } catch (err) {
        console.error(`[RecordButton] Chunk ${index + chunkOffsetRef.current} upload failed:`, err);
      }
    },
    [sessionId]
  );

  const handleRecordingComplete = useCallback(
    async (blob: Blob, durationSeconds: number) => {
      // Do NOT trigger transcription — just register the recording and show Record More / Done
      setUploading(true);
      setUploadError(null);
      setUploadProgress(0);
      const phase = activePhaseRef.current;

      try {
        // Store blob reference for potential "Done" upload
        lastBlobRef.current = blob;
        lastSegmentDurationRef.current = durationSeconds;

        // Accumulate total duration across segments
        setTotalDurationSeconds(prev => prev + durationSeconds);
        setSegmentCount(prev => prev + 1);
        hasSegmentRef.current = true;

        // Register recording in DB with status 'uploaded' (no transcription trigger)
        await registerRecording({
          sessionId,
          phase,
          durationSeconds: totalDurationSeconds + durationSeconds,
          chunkCount: chunksUploadedRef.current,
        });
      } catch (err) {
        const message = (err as Error).message || 'Upload failed';
        setUploadError(message);
        onError?.(message);
      } finally {
        setUploading(false);
      }
    },
    [sessionId, onError, totalDurationSeconds]
  );

  const handleError = useCallback(
    (err: RecorderError) => {
      onError?.(err.message);
    },
    [onError]
  );

  const recorder = useAudioRecorder({
    chunkIntervalMs: 30000,
    onChunkAvailable: handleChunkAvailable,
    onRecordingComplete: handleRecordingComplete,
    onError: handleError,
  });

  const isActive = recorder.state === 'recording' || recorder.state === 'paused';
  const isRecording = recorder.state === 'recording';
  const isPaused = recorder.state === 'paused';
  const isStopped = recorder.state === 'stopped';
  const hasError = recorder.state === 'error' || !!uploadError;
  // Phase selector is locked during recording, uploading, OR after first segment until "Done"
  const isLocked = isActive || uploading || (hasSegmentRef.current && !finalized);

  const handleStart = useCallback(async () => {
    activePhaseRef.current = selectedPhase;
    setUploadError(null);
    setChunksUploaded(0);
    chunksUploadedRef.current = 0;
    chunkOffsetRef.current = 0;
    setTotalDurationSeconds(0);
    setSegmentCount(0);
    setFinalized(false);
    hasSegmentRef.current = false;
    lastBlobRef.current = null;
    lastSegmentDurationRef.current = 0;
    onRecordingStart?.();
    await recorder.start();
  }, [recorder, onRecordingStart, selectedPhase]);

  // "Record More" — start a new recording segment, offset chunk indices
  const handleRecordMore = useCallback(async () => {
    chunkOffsetRef.current = chunksUploadedRef.current;
    setUploadError(null);
    lastBlobRef.current = null;
    lastSegmentDurationRef.current = 0;
    await recorder.start();
  }, [recorder]);

  // "Done" — upload final complete blob, then phase-specific pipeline:
  //   briefing:   upload only (no transcription — wait for simulator)
  //   simulator:  upload + concatenate briefing+sim → transcribe as 'briefing_sim'
  //   debriefing: upload + concatenate all → transcribe as 'full'
  const handleDone = useCallback(async () => {
    setFinalizing(true);
    setUploadError(null);
    const phase = activePhaseRef.current;
    const finalDuration = totalDurationSeconds;

    try {
      let recordingId: string;

      if (lastBlobRef.current) {
        // Upload the last segment's complete blob
        const result = await uploadCompleteRecording({
          blob: lastBlobRef.current,
          sessionId,
          phase,
          durationSeconds: finalDuration,
          chunkCount: chunksUploadedRef.current,
          onProgress: (info) => setUploadProgress(info.progress),
        });
        recordingId = result.recordingId;
      } else {
        // Fallback: no blob available, get recordingId from DB
        const { supabase } = await import('../lib/supabase');
        const { data } = await supabase
          .from('session_recordings')
          .select('id')
          .eq('session_id', sessionId)
          .eq('phase', phase)
          .single();
        recordingId = data?.id || '';
      }

      // Phase-specific pipeline: concatenation + transcription
      if (phase === 'briefing') {
        // Briefing: upload only — transcription happens after simulator recording
        console.log(`[RecordButton] Briefing uploaded. Waiting for simulator recording.`);
      } else if (phase === 'simulator') {
        // Simulator: concatenate briefing+sim → transcribe as 'briefing_sim'
        console.log(`[RecordButton] Concatenating briefing+simulator...`);
        const concat = await concatenateRecordings(sessionId, 'briefing_sim');
        console.log(`[RecordButton] Triggering transcription for briefing_sim...`);
        await triggerTranscription(
          sessionId,
          'briefing_sim',
          concat.signedUrl,
          concat.recordingId
        );
      } else if (phase === 'debriefing') {
        // Debriefing: concatenate all phases → transcribe as 'full'
        console.log(`[RecordButton] Concatenating all phases...`);
        const concat = await concatenateRecordings(sessionId, 'full');
        console.log(`[RecordButton] Triggering transcription for full...`);
        await triggerTranscription(
          sessionId,
          'full',
          concat.signedUrl,
          concat.recordingId
        );
      }

      setFinalized(true);
      hasSegmentRef.current = false;
      onRecordingComplete?.(recordingId, phase);
    } catch (err) {
      const message = (err as Error).message || 'Failed to finalize recording';
      setUploadError(message);
      onError?.(message);
    } finally {
      setFinalizing(false);
      setUploadProgress(0);
    }
  }, [sessionId, totalDurationSeconds, onRecordingComplete, onError]);

  // Audio level drives ring intensity and glow
  const level = isRecording ? recorder.audioLevel : 0;

  // Sizing — large touch targets for gloved fingers
  const outerSize = 104;
  const ringSize = 96;
  const buttonSize = 80;

  // Dynamic ring values
  const ringOpacity = 0.7 + level * 0.3;
  const glowSpread = 20 + level * 30;
  const glowAlpha = 0.25 + level * 0.35;
  const spinDuration = Math.max(1.2, 3 - level * 2);

  // Display duration: during recording, show accumulated + current segment; when stopped, show total
  const displayDuration = isActive
    ? totalDurationSeconds + recorder.durationSeconds
    : totalDurationSeconds;

  // Show "Record More / Done" when stopped, upload finished, no error, and not yet finalized
  const showRecordMoreDone = isStopped && !uploading && !uploadError && !finalized && !finalizing;

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Phase selector pills — hidden when defaultPhase is set (wizard controls phase) */}
      {!defaultPhase && (
      <div className="flex items-center gap-1 rounded-full p-1" style={{ background: '#e2e8f0' }}>
        {PHASES.map((p) => {
          const isSelected = selectedPhase === p.key;
          return (
            <button
              key={p.key}
              onClick={() => !isLocked && setSelectedPhase(p.key)}
              disabled={isLocked}
              className="px-3 py-1.5 text-xs font-semibold rounded-full transition-all duration-200"
              style={{
                background: isSelected
                  ? isRecording
                    ? '#dc2626'
                    : isPaused
                      ? '#d97706'
                      : '#0B2C45'
                  : 'transparent',
                color: isSelected
                  ? '#ffffff'
                  : isLocked
                    ? '#94a3b8'
                    : '#475569',
                cursor: isLocked ? 'not-allowed' : 'pointer',
                boxShadow: isSelected ? '0 1px 3px rgba(0,0,0,0.15)' : 'none',
              }}
            >
              <span style={{ marginRight: 4 }}>{p.icon}</span>
              {p.label}
            </button>
          );
        })}
      </div>
      )}

      {/* Main button assembly — the full outer area is clickable */}
      {!showRecordMoreDone && (
        <div
          className="relative flex items-center justify-center cursor-pointer select-none"
          style={{ width: outerSize, height: outerSize }}
          onClick={
            isActive
              ? recorder.stop
              : uploading || finalizing
                ? undefined
                : finalized
                  ? undefined
                  : handleStart
          }
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (isActive) recorder.stop();
              else if (!uploading && !finalizing && !finalized) handleStart();
            }
          }}
          aria-label={
            isActive
              ? 'Stop recording'
              : uploading || finalizing
                ? 'Processing...'
                : `Start ${selectedPhase} recording`
          }
        >
          {/* Layer 1: Ambient glow (recording only) */}
          <div
            className="absolute inset-0 rounded-full transition-all duration-300"
            style={{
              opacity: isRecording ? 1 : 0,
              boxShadow: isRecording
                ? `0 0 ${glowSpread}px rgba(239, 68, 68, ${glowAlpha}), 0 0 ${glowSpread * 2}px rgba(239, 68, 68, ${glowAlpha * 0.4})`
                : 'none',
              animation: isRecording ? 'cvr-glow 2s ease-in-out infinite' : 'none',
            }}
          />

          {/* Layer 2: Ring — idle: static border ring, recording: spinning CVR, paused: slow amber */}
          <div
            className="absolute rounded-full"
            style={{
              width: ringSize,
              height: ringSize,
              top: (outerSize - ringSize) / 2,
              left: (outerSize - ringSize) / 2,
              opacity: isActive ? ringOpacity : 1,
              transition: 'opacity 0.3s ease',
              background: isRecording
                ? `conic-gradient(from 0deg, #ef4444, #dc2626, #b91c1c, transparent 30%, transparent 70%, #ef4444)`
                : isPaused
                  ? `conic-gradient(from 0deg, #f59e0b, #d97706, transparent 30%, transparent 70%, #f59e0b)`
                  : 'transparent',
              border: isActive ? 'none' : '2.5px solid #94a3b8',
              animation: isRecording
                ? `cvr-ring-spin ${spinDuration}s linear infinite`
                : isPaused
                  ? 'cvr-ring-spin 6s linear infinite'
                  : 'none',
            }}
          />

          {/* Layer 3: Inner mask — creates ring shape during recording/paused */}
          {isActive && (
            <div
              className="absolute rounded-full"
              style={{
                width: ringSize - 8,
                height: ringSize - 8,
                top: (outerSize - ringSize + 8) / 2,
                left: (outerSize - ringSize + 8) / 2,
                background: '#1a1a2e',
              }}
            />
          )}

          {/* Layer 4: Core button */}
          <button
            disabled={uploading || finalizing || recorder.state === 'stopping'}
            className="relative rounded-full flex items-center justify-center focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-400/50"
            style={{
              width: buttonSize,
              height: buttonSize,
              minWidth: buttonSize,
              minHeight: buttonSize,
              background: isRecording
                ? '#dc2626'
                : isPaused
                  ? '#d97706'
                  : hasError
                    ? '#fee2e2'
                    : uploading || finalizing
                      ? '#374151'
                      : '#1e293b',
              color: isRecording || isPaused
                ? '#ffffff'
                : hasError
                  ? '#dc2626'
                  : uploading || finalizing
                    ? '#9ca3af'
                    : '#94a3b8',
              border: isActive
                ? 'none'
                : hasError
                  ? '2px solid #fca5a5'
                  : '2px solid #334155',
              boxShadow: isRecording
                ? `0 0 ${12 + level * 20}px rgba(239, 68, 68, ${0.4 + level * 0.3})`
                : 'none',
              transition: 'all 0.3s ease',
              pointerEvents: 'none',
            }}
            tabIndex={-1}
          >
            {uploading || finalizing ? (
              <div className="w-7 h-7 border-3 border-gray-500 border-t-transparent rounded-full animate-spin" />
            ) : isActive ? (
              /* Stop icon — rounded square */
              <div
                className="rounded-sm"
                style={{
                  width: buttonSize * 0.3,
                  height: buttonSize * 0.3,
                  background: '#ffffff',
                }}
              />
            ) : (
              /* Mic icon — matching the section header style */
              <span className="text-2xl" role="img" aria-label="Microphone">{'\ud83c\udfa4\ufe0f'}</span>
            )}
          </button>
        </div>
      )}

      {/* Stopped state — green checkmark circle for Record More / Done */}
      {showRecordMoreDone && (
        <div
          className="relative flex items-center justify-center"
          style={{ width: outerSize, height: outerSize }}
        >
          <div
            className="rounded-full flex items-center justify-center"
            style={{
              width: buttonSize,
              height: buttonSize,
              background: '#059669',
              border: 'none',
            }}
          >
            <span className="text-white text-3xl font-bold">{'\u2713'}</span>
          </div>
        </div>
      )}

      {/* Timer — always visible, shows cumulative total */}
      <span
        className="text-xl font-mono font-bold tracking-wider"
        style={{
          fontVariantNumeric: 'tabular-nums',
          color: isRecording ? '#dc2626' : isPaused ? '#d97706' : showRecordMoreDone ? '#059669' : '#94a3b8',
        }}
      >
        {formatDuration(displayDuration)}
      </span>

      {/* Status indicators (recording/paused) */}
      {isActive && (
        <div className="flex flex-col items-center gap-1.5">
          {/* REC / PAUSED */}
          <div className="flex items-center gap-3 text-xs">
            {isRecording && (
              <span
                className="flex items-center gap-1.5 text-red-500 font-bold uppercase tracking-widest"
                style={{ animation: 'cvr-rec-blink 1s ease-in-out infinite' }}
              >
                <span
                  className="inline-block w-2.5 h-2.5 bg-red-500 rounded-full"
                  style={{ boxShadow: '0 0 6px rgba(239, 68, 68, 0.8)' }}
                />
                REC
              </span>
            )}
            {isPaused && (
              <span className="flex items-center gap-1.5 text-amber-500 font-bold uppercase tracking-widest">
                <span className="inline-block w-2.5 h-2.5 bg-amber-500 rounded-full" />
                PAUSED
              </span>
            )}
            {chunksUploaded > 0 && (
              <span className="text-slate-500 font-medium">
                {chunksUploaded} chunk{chunksUploaded !== 1 ? 's' : ''} saved
              </span>
            )}
          </div>

          {/* Pause / Resume controls */}
          <div className="flex items-center gap-2 mt-1">
            {isRecording && (
              <button
                onClick={(e) => { e.stopPropagation(); recorder.pause(); }}
                className="px-4 py-1.5 text-xs bg-amber-900/30 hover:bg-amber-900/50 text-amber-400 rounded-full font-semibold uppercase tracking-wide transition-colors border border-amber-700/40"
              >
                Pause
              </button>
            )}
            {isPaused && (
              <button
                onClick={(e) => { e.stopPropagation(); recorder.resume(); }}
                className="px-4 py-1.5 text-xs bg-green-900/30 hover:bg-green-900/50 text-green-400 rounded-full font-semibold uppercase tracking-wide transition-colors border border-green-700/40"
              >
                Resume
              </button>
            )}
          </div>
        </div>
      )}

      {/* Record More / Done stopped state */}
      {showRecordMoreDone && (
        <div className="flex flex-col items-center gap-3">
          <span className="text-sm text-slate-500 font-medium">
            {chunksUploaded} chunk{chunksUploaded !== 1 ? 's' : ''} saved
            {segmentCount > 1 ? ` (${segmentCount} segments)` : ''}
          </span>
          <span className="text-sm text-green-600 font-semibold">Upload complete</span>
          <div className="flex items-center gap-3 mt-1">
            <button
              onClick={handleRecordMore}
              className="px-5 py-2 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full font-medium transition-colors border border-slate-300"
            >
              Record More
            </button>
            <button
              onClick={handleDone}
              className="px-6 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-full font-semibold transition-colors"
            >
              Done {'\u2713'}
            </button>
          </div>
        </div>
      )}

      {/* Idle hint */}
      {!isActive && !uploading && !finalizing && !isStopped && !hasError && !finalized && (
        <span className="text-xs text-slate-400 font-medium tracking-wide">
          Tap to start recording
        </span>
      )}

      {/* Uploading state with real progress */}
      {(uploading || finalizing) && (
        <div className="flex flex-col items-center gap-2 w-48">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
            <span>
              {finalizing ? 'Finalizing...' : 'Uploading recording...'}{' '}
              {uploadProgress > 0 ? `${uploadProgress}%` : ''}
            </span>
          </div>
          {uploadProgress > 0 && (
            <div className="w-full bg-slate-700 rounded-full h-1.5">
              <div
                className="h-1.5 rounded-full bg-blue-500 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Finalized success state */}
      {finalized && !uploading && !finalizing && !hasError && (
        <div className="text-sm text-green-600 font-semibold">
          {activePhaseRef.current === 'briefing'
            ? 'Briefing saved'
            : 'Recording saved & transcription started'}
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div className="flex flex-col items-center gap-2 max-w-xs">
          <p className="text-sm text-red-500 text-center">
            {uploadError || recorder.error?.message || 'An error occurred'}
          </p>
          {recorder.error?.code === 'NOT_ALLOWED' && (
            <p className="text-xs text-slate-500 text-center">
              Check your browser settings: click the lock icon in the address bar
              and allow microphone access for this site.
            </p>
          )}
          <button
            onClick={handleStart}
            className="px-4 py-1.5 text-xs bg-red-100 hover:bg-red-200 text-red-600 rounded-full font-semibold uppercase tracking-wide transition-colors border border-red-300"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}
